import { Fragment, type ReactNode } from "react";
import { Info, Lightbulb, Sparkles, AlertTriangle, Gamepad2 } from "lucide-react";

/**
 * Minimal, dependency-free Markdown renderer for the study library. Supports
 * headings, paragraphs, lists, blockquotes, GFM pipe tables, callout blocks
 * (> [!TYPE] title), and inline **bold**, *italic*, `code`, [links](url).
 *
 * Highlights: each saved highlight targets a *specific* occurrence of its text
 * (by 0-based index) so highlighting one "design" doesn't mark them all. A shared
 * counter walks every visible text run left-to-right, top-to-bottom (matching the
 * DOM order used when the highlight was captured), and marks only the intended
 * occurrence — in the highlight's chosen color.
 */

export interface MarkHighlight {
  id: string;
  text: string;
  comment?: string;
  color?: string;
  occurrence?: number;
}

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

export const HL_COLORS: Record<string, string> = {
  yellow: "bg-amber-300/40",
  green: "bg-emerald-300/40",
  blue: "bg-sky-300/40",
  pink: "bg-pink-300/40",
  purple: "bg-violet-300/40",
};

/** A stateful highlight context: shared occurrence counter + text lookup. */
interface HLCtx {
  active: boolean;
  scan: (text: string, keyBase: string) => ReactNode[];
  count: (text: string) => void;
}

/** Wrap every case-insensitive match of the search query in a distinct mark. */
function emphQuery(text: string, q: string, keyBase: string): ReactNode[] {
  if (!q) return [<Fragment key={keyBase}>{text}</Fragment>];
  const nodes: ReactNode[] = [];
  const low = text.toLowerCase();
  let pos = 0;
  let n = 0;
  let i = low.indexOf(q);
  while (i !== -1) {
    if (i > pos) nodes.push(<Fragment key={`${keyBase}-q${n++}`}>{text.slice(pos, i)}</Fragment>);
    nodes.push(
      <mark key={`${keyBase}-q${n++}`} className="rounded bg-primary/25 text-text ring-1 ring-primary/40">
        {text.slice(i, i + q.length)}
      </mark>
    );
    pos = i + q.length;
    i = low.indexOf(q, pos);
  }
  if (pos < text.length) nodes.push(<Fragment key={`${keyBase}-q${n++}`}>{text.slice(pos)}</Fragment>);
  return nodes;
}

function makeCtx(highlights: MarkHighlight[], query = ""): HLCtx {
  const list = highlights.filter((h) => h.text);
  const q = query.trim().toLowerCase();
  if (list.length === 0 && !q) return { active: false, scan: (t) => [t], count: () => {} };

  const byText = new Map<string, Map<number | "all", MarkHighlight>>();
  for (const h of list) {
    if (!byText.has(h.text)) byText.set(h.text, new Map());
    byText.get(h.text)!.set(h.occurrence ?? "all", h);
  }
  const texts = [...byText.keys()];
  const seen: Record<string, number> = {};

  const count = (text: string) => {
    for (const t of texts) {
      let i = text.indexOf(t);
      while (i !== -1) {
        seen[t] = (seen[t] ?? 0) + 1;
        i = text.indexOf(t, i + t.length);
      }
    }
  };

  const scan = (text: string, keyBase: string): ReactNode[] => {
    const nodes: ReactNode[] = [];
    let pos = 0;
    let n = 0;
    while (pos < text.length) {
      let best = -1;
      let bestText = "";
      for (const t of texts) {
        const i = text.indexOf(t, pos);
        if (i !== -1 && (best === -1 || i < best || (i === best && t.length > bestText.length))) {
          best = i;
          bestText = t;
        }
      }
      if (best === -1) {
        nodes.push(...emphQuery(text.slice(pos), q, `${keyBase}-t${n++}`));
        break;
      }
      if (best > pos) nodes.push(...emphQuery(text.slice(pos, best), q, `${keyBase}-t${n++}`));
      const occ = seen[bestText] ?? 0;
      seen[bestText] = occ + 1;
      const hl = byText.get(bestText)!.get(occ) ?? byText.get(bestText)!.get("all");
      if (hl) {
        const cls = HL_COLORS[hl.color ?? "yellow"] ?? HL_COLORS.yellow;
        nodes.push(
          <mark
            key={`${keyBase}-h${n++}`}
            title={hl.comment || undefined}
            className={[
              "rounded px-0.5 text-text",
              cls,
              hl.comment ? "cursor-help underline decoration-black/40 decoration-dotted underline-offset-2" : "",
            ].join(" ")}
          >
            {bestText}
          </mark>
        );
      } else {
        nodes.push(...emphQuery(bestText, q, `${keyBase}-h${n++}`));
      }
      pos = best + bestText.length;
    }
    return nodes;
  };

  return { active: true, scan, count };
}

function renderInline(text: string, ctx: HLCtx): ReactNode[] {
  const parts = text.split(INLINE).filter((p) => p !== "");
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const inner = part.slice(2, -2);
      return <strong key={i} className="font-semibold text-text">{ctx.active ? ctx.scan(inner, `b${i}`) : inner}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      const inner = part.slice(1, -1);
      return <em key={i}>{ctx.active ? ctx.scan(inner, `i${i}`) : inner}</em>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      const inner = part.slice(1, -1);
      ctx.count(inner); // keep the occurrence counter aligned without marking code
      return (
        <code key={i} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.85em] text-text">{inner}</code>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      ctx.count(link[1]);
      return (
        <a key={i} href={link[2]} target="_blank" rel="noreferrer" className="text-primary underline">{link[1]}</a>
      );
    }
    return <Fragment key={i}>{ctx.active ? ctx.scan(part, String(i)) : [part]}</Fragment>;
  });
}

const CALLOUTS: Record<string, { icon: typeof Info; ring: string; tint: string; accent: string }> = {
  NOTE: { icon: Info, ring: "border-accent/40", tint: "bg-accent/5", accent: "text-accent" },
  INFO: { icon: Info, ring: "border-accent/40", tint: "bg-accent/5", accent: "text-accent" },
  TIP: { icon: Lightbulb, ring: "border-emerald-400/40", tint: "bg-emerald-400/5", accent: "text-emerald-400" },
  INSIGHT: { icon: Sparkles, ring: "border-primary/40", tint: "bg-primary/5", accent: "text-primary" },
  IMPORTANT: { icon: Sparkles, ring: "border-primary/40", tint: "bg-primary/5", accent: "text-primary" },
  WARNING: { icon: AlertTriangle, ring: "border-amber-400/40", tint: "bg-amber-400/5", accent: "text-amber-400" },
  CAUTION: { icon: AlertTriangle, ring: "border-amber-400/40", tint: "bg-amber-400/5", accent: "text-amber-400" },
  EXAMPLE: { icon: Gamepad2, ring: "border-accent/40", tint: "bg-accent/5", accent: "text-accent" },
};

function isTableSeparator(line: string): boolean {
  const t = line.trim();
  return t.includes("|") && t.includes("-") && /^[\s|:-]+$/.test(t);
}
function parseRow(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

function renderBlocks(lines: string[], ctx: HLCtx, keyPrefix = ""): ReactNode[] {
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;
  const k = () => `${keyPrefix}${key++}`;

  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") { i++; continue; }

    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const content = renderInline(h[2], ctx);
      if (level === 1) blocks.push(<h2 key={k()} className="mt-6 font-display text-2xl font-bold text-text">{content}</h2>);
      else if (level === 2) blocks.push(<h3 key={k()} className="mt-6 font-display text-lg font-semibold text-text">{content}</h3>);
      else blocks.push(<h4 key={k()} className="mt-4 font-display text-base font-semibold text-text">{content}</h4>);
      i++;
      continue;
    }

    if (line.trim().startsWith("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = parseRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { rows.push(parseRow(lines[i])); i++; }
      blocks.push(
        <div key={k()} className="my-3 overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse text-sm">
            <thead><tr>{header.map((cell, n) => <th key={n} className="border-b border-border bg-surface-2 px-3 py-2 text-left font-semibold text-text">{renderInline(cell, ctx)}</th>)}</tr></thead>
            <tbody>{rows.map((row, r) => <tr key={r} className="odd:bg-surface-2/30">{row.map((cell, c) => <td key={c} className="border-t border-border px-3 py-2 align-top text-muted">{renderInline(cell, ctx)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { quoted.push(lines[i].replace(/^>\s?/, "")); i++; }
      const callout = /^\[!(\w+)\]\s*(.*)$/.exec(quoted[0]);
      if (callout) {
        const type = callout[1].toUpperCase();
        const style = CALLOUTS[type] ?? CALLOUTS.NOTE;
        const Icon = style.icon;
        const title = callout[2].trim() || type.charAt(0) + type.slice(1).toLowerCase();
        // Keep the occurrence counter aligned with capture (which sees the title in the DOM).
        ctx.count(title);
        blocks.push(
          <div key={k()} className={`my-3 rounded-xl border ${style.ring} ${style.tint} p-4`}>
            <div className={`mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${style.accent}`}><Icon className="h-4 w-4" />{title}</div>
            <div className="text-sm">{renderBlocks(quoted.slice(1), ctx, `${k()}-`)}</div>
          </div>
        );
      } else {
        blocks.push(<blockquote key={k()} className="mt-3 border-l-2 border-primary/50 pl-4 italic text-muted">{renderInline(quoted.join(" "), ctx)}</blockquote>);
      }
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^[-*]\s+/, "")); i++; }
      blocks.push(<ul key={k()} className="mt-2 ml-5 list-disc space-y-1 text-muted">{items.map((it, n) => <li key={n}>{renderInline(it, ctx)}</li>)}</ul>);
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s+/, "")); i++; }
      blocks.push(<ol key={k()} className="mt-2 ml-5 list-decimal space-y-1 text-muted">{items.map((it, n) => <li key={n}>{renderInline(it, ctx)}</li>)}</ol>);
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !(lines[i].trim().startsWith("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i++;
    }
    if (para.length) blocks.push(<p key={k()} className="mt-3 leading-relaxed text-muted">{renderInline(para.join(" "), ctx)}</p>);
  }

  return blocks;
}

export default function Markdown({
  source,
  highlights = [],
  query = "",
}: {
  source: string;
  highlights?: MarkHighlight[];
  /** When set, case-insensitive matches are visually emphasized (reading-pane search). */
  query?: string;
}) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const ctx = makeCtx(highlights, query);
  return <div>{renderBlocks(lines, ctx)}</div>;
}
